import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced validation of environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const missingVars = [];
  if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missingVars.push('VITE_SUPABASE_ANON_KEY');
  
  const errorMessage = `Missing required Supabase environment variables: ${missingVars.join(', ')}. Please ensure you have connected your Supabase project by clicking the "Connect to Supabase" button in the top right corner.`;
  console.error('❌ Supabase configuration error:', errorMessage);
  throw new Error(errorMessage);
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  const errorMessage = `Invalid Supabase URL format: ${supabaseUrl}. Please ensure you have connected your Supabase project correctly.`;
  console.error('❌ Supabase configuration error:', errorMessage);
  throw new Error(errorMessage);
}

// Validate anon key format (basic check)
if (!supabaseAnonKey.startsWith('eyJ') || supabaseAnonKey.length < 100) {
  const errorMessage = 'Invalid Supabase anon key format. Please ensure you have connected your Supabase project correctly.';
  console.error('❌ Supabase configuration error:', errorMessage);
  throw new Error(errorMessage);
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'sb-auth-token',
    flowType: 'pkce',
  },
});

// Enhanced connection test with detailed error reporting
export const testConnection = async () => {
  try {
    const { count, error } = await supabase
      .from('articles')
      .select('*', { count: 'exact', head: true })
      .eq('published', true);
    
    if (error) {
      if (error.code === '401') {
        console.error('❌ Supabase authentication error: Invalid API key. Please reconnect your Supabase project.');
      } else {
        console.error('❌ Supabase query error:', error.message);
      }
      return false;
    }
    
    console.log('✅ Supabase connection successful');
    return true;
  } catch (error) {
    console.error('❌ Supabase connection error:', error);
    return false;
  }
};

// Get current session safely with validation
export const getCurrentSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Session error:', error);
      return null;
    }
    
    // Validate session has required claims
    if (!session?.user?.id || !session?.access_token) {
      console.warn('Invalid session data:', { 
        hasUserId: !!session?.user?.id,
        hasAccessToken: !!session?.access_token 
      });
      return null;
    }
    
    return session;
  } catch (error) {
    console.error('Error getting session:', error);
    return null;
  }
};

// Get current user safely with validation
export const getCurrentUser = async () => {
  try {
    const session = await getCurrentSession();
    if (!session?.user?.id) {
      return null;
    }
    
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user?.id) {
      console.error('User fetch error:', error);
      return null;
    }

    return user;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

// Refresh session with enhanced validation
export const refreshSession = async () => {
  try {
    // First check if we have a current session
    const currentSession = await getCurrentSession();
    
    // If no current session, don't attempt refresh
    if (!currentSession) {
      console.warn('No current session to refresh');
      return null;
    }

    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('Session refresh error:', error);
      // Clear invalid session data
      await supabase.auth.signOut();
      return null;
    }

    // Enhanced session validation
    if (!session?.user?.id || !session?.access_token) {
      console.warn('Invalid refreshed session data:', {
        hasUserId: !!session?.user?.id,
        hasAccessToken: !!session?.access_token
      });
      await supabase.auth.signOut();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error refreshing session:', error);
    return null;
  }
};

// Handle auth state changes with enhanced validation
supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('Auth state change:', event, {
    hasSession: !!session,
    hasUserId: !!session?.user?.id,
    hasAccessToken: !!session?.access_token
  });

  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    // Clear auth data
    localStorage.removeItem('sb-auth-token');
    sessionStorage.clear();
  } else if (event === 'TOKEN_REFRESHED') {
    if (!session?.user?.id || !session?.access_token) {
      console.warn('Invalid token refresh data');
      await supabase.auth.signOut();
    }
  }
});

// Test connection on client initialization
testConnection().then(isConnected => {
  if (!isConnected) {
    console.error('❌ Failed to establish Supabase connection. Please check your configuration.');
  }
});

export default supabase;